import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { calendarApi } from './api';
import { formatRange } from './calendarTime';
import { CloseX, useCloseGuard } from './calendarModal';

// =============================================================================
// 5.43.0 "📥 Import .ics" — upload or drop an iCalendar file, preview it, import the ticked events.
// The server does everything that matters (services/calendar/ics_import.py): .ics only, 5 MB / 500 events, calendars
// this login may import into (403 otherwise), every time converted to Central (a repeating series that would land
// differently in Central keeps its own zone and is flagged), duplicates by UID skipped, attendees / organizer removed,
// tasks and cancellation files not imported, one audit row per import. Nothing is written before "Import".
// Closes only by its buttons (Cancel / Close / ✕); a loaded file not yet imported asks "Discard changes?".
// =============================================================================

export const MAX_BYTES = 5 * 1024 * 1024;
export const isIcsFile = (f) => !!f && (/\.ics$/i.test(f.name || '') || (f.type || '').toLowerCase() === 'text/calendar');

const C = { bg: '#1a1a1a', panel: '#242424', border: '#3a3a3a', text: '#f0f0f0', dim: '#aaa', violet: '#7c3aed' };
const btn = (bg, off) => ({ padding: '8px 16px', border: 'none', borderRadius: 6, background: bg, color: '#fff', fontSize: 13, fontWeight: 600, cursor: off ? 'default' : 'pointer', opacity: off ? 0.5 : 1 });
const tag = (bg) => ({ display: 'inline-block', padding: '0 6px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#fff', background: bg, marginRight: 4, whiteSpace: 'nowrap' });
const detail = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';

export function checkFile(f) {
  if (!isIcsFile(f)) return 'Only .ics calendar files can be imported';
  if (f.size > MAX_BYTES) return 'That file is larger than 5 MB';
  if (f.size === 0) return 'That file is empty';
  return null;
}

export default function CalendarImport({ initialFile, onClose, onImported }) {
  const [targets, setTargets] = useState(null);
  const [calendarId, setCalendarId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [over, setOver] = useState(false);

  const dirty = !!file && !result;
  const { requestClose, prompt } = useCloseGuard(dirty, onClose);

  useEffect(() => {
    calendarApi.importTargets().then((r) => {
      setTargets(r.data.calendars);
      if (r.data.calendars.length) setCalendarId(String(r.data.calendars[0].id));
    }).catch((e) => setErr(detail(e)));
  }, []);

  const pick = useCallback((f) => {
    setResult(null); setPreview(null); setErr('');
    const bad = checkFile(f);
    if (bad) { setFile(null); setErr(bad); return; }
    setFile(f);
  }, []);
  useEffect(() => { if (initialFile) pick(initialFile); }, [initialFile, pick]);

  useEffect(() => {                                     // (re)preview when the file or the calendar changes
    if (!file || !calendarId) return undefined;
    let live = true;
    setBusy(true); setErr(''); setPreview(null);
    calendarApi.importPreview(file, calendarId).then((r) => {
      if (!live) return;
      setPreview(r.data);
      setSel(new Set(r.data.events.filter((e) => !e.duplicate && !e.error).map((e) => e.uid)));
    }).catch((e) => { if (live) setErr(detail(e)); }).finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [file, calendarId]);

  const importable = useMemo(() => (preview ? preview.events.filter((e) => !e.duplicate && !e.error) : []), [preview]);
  const toggle = (uid) => setSel((s) => { const n = new Set(s); if (n.has(uid)) n.delete(uid); else n.add(uid); return n; });
  const run = async () => {
    setBusy(true); setErr('');
    try {
      const r = await calendarApi.importRun(file, calendarId, [...sel]);
      setResult(r.data);
      if (onImported) onImported(r.data);
    } catch (e) { setErr(detail(e)); } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setOver(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) pick(f);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      data-testid="ics-import-overlay" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <div role="dialog" aria-label="Import .ics" data-testid="ics-import"
        style={{ background: C.panel, color: C.text, borderRadius: 10, width: 860, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 17, fontWeight: 700, flex: 1 }}>📥 Import .ics</div>
          <CloseX onClick={requestClose} disabled={busy} testid="ics-import-x" />
        </div>
        <div style={{ padding: '14px 18px', overflowY: 'auto', display: 'grid', gap: 12 }}>
          {!result && (
            <>
              <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop} data-testid="ics-dropzone"
                style={{ border: `2px dashed ${over ? C.violet : '#555'}`, borderRadius: 10, padding: 18, textAlign: 'center', background: over ? '#2e1065' : C.bg }}>
                <div style={{ fontSize: 14, marginBottom: 8 }}>{file ? <b data-testid="ics-file-name">{file.name}</b> : 'Drop an .ics file here'}</div>
                <label style={{ ...btn('#334155', false), display: 'inline-block' }}>
                  {file ? 'Choose another file…' : 'Choose a file…'}
                  <input type="file" accept=".ics,text/calendar" style={{ display: 'none' }} data-testid="ics-file-input"
                    onChange={(e) => { if (e.target.files[0]) pick(e.target.files[0]); e.target.value = ''; }} />
                </label>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>.ics files up to 5 MB (Google, Outlook, iPhone exports). All times become Central.</div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                Import into
                <select value={calendarId} onChange={(e) => setCalendarId(e.target.value)} disabled={busy || !targets} data-testid="ics-calendar"
                  style={{ padding: '6px 8px', background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, minWidth: 220 }}>
                  {(targets || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {targets && targets.length === 0 && <span style={{ color: '#fbbf24' }}>You have no calendar to import into.</span>}
              </label>
            </>
          )}
          {err && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '8px 10px', borderRadius: 6, fontSize: 13 }} data-testid="ics-error">{err}</div>}
          {busy && !preview && !result && <div style={{ color: C.dim, fontSize: 13 }}>Reading the file…</div>}
          {preview && !result && (
            <div data-testid="ics-preview">
              {preview.cancel && <div style={{ background: '#78350f', color: '#fde68a', padding: '8px 10px', borderRadius: 6, fontSize: 13 }} data-testid="ics-cancel">{preview.message}</div>}
              {preview.events.length > 0 && (
                <>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: C.dim, margin: '4px 0 6px' }}>
                    <span>{preview.events.length} event(s) · {sel.size} selected</span>
                    <button type="button" onClick={() => setSel(new Set(importable.map((e) => e.uid)))} data-testid="ics-select-all" style={{ ...btn('#334155', false), padding: '3px 10px', fontSize: 12 }}>Select all</button>
                    <button type="button" onClick={() => setSel(new Set())} data-testid="ics-select-none" style={{ ...btn('#334155', false), padding: '3px 10px', fontSize: 12 }}>Select none</button>
                  </div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}>
                    {preview.events.map((e) => (
                      <label key={e.uid} data-testid="ics-row" data-uid={e.uid} data-duplicate={e.duplicate ? '1' : '0'} data-kept={e.kept_zone ? '1' : '0'}
                        style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr) 260px', gap: 10, alignItems: 'start', padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontSize: 13, opacity: e.duplicate || e.error ? 0.6 : 1 }}>
                        <input type="checkbox" checked={sel.has(e.uid)} disabled={e.duplicate || !!e.error} onChange={() => toggle(e.uid)} data-testid="ics-row-check" />
                        <span style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 600, wordBreak: 'break-word' }} data-testid="ics-row-title">{e.title}</span>
                          <span style={{ display: 'block', marginTop: 2 }}>
                            {e.all_day && <span style={tag('#0e7490')} data-testid="ics-allday">All day</span>}
                            {e.recurring && <span style={tag('#4338ca')} data-testid="ics-recurring">🔁 Repeats</span>}
                            {e.duplicate && <span style={tag('#64748b')} data-testid="ics-duplicate">Already in this calendar</span>}
                            {e.kept_zone && <span style={tag('#b45309')} data-testid="ics-kept" title={e.kept_reason}>⚠ Kept in {e.original_zone}</span>}
                            {e.attendees_removed > 0 && <span style={{ fontSize: 11, color: C.dim, marginRight: 6 }}>{e.attendees_removed} invitee(s) removed</span>}
                            {e.alarms > 0 && <span style={{ fontSize: 11, color: C.dim, marginRight: 6 }}>⏰ {e.alarms} reminder(s)</span>}
                            {(e.exdates > 0 || e.overrides > 0) && <span style={{ fontSize: 11, color: C.dim }}>{e.exdates ? `${e.exdates} skipped date(s)` : ''}{e.exdates && e.overrides ? ' · ' : ''}{e.overrides ? `${e.overrides} changed occurrence(s)` : ''}</span>}
                            {e.error && <span style={{ color: '#f87171', fontSize: 12 }} data-testid="ics-row-error">Can't import: {e.error}</span>}
                          </span>
                          {e.kept_zone && <span style={{ display: 'block', fontSize: 11, color: '#fbbf24' }}>{e.kept_reason}</span>}
                        </span>
                        <span style={{ fontSize: 12 }}>
                          <span data-testid="ics-row-when">{e.start ? formatRange(e.start, e.end || e.start, e.all_day) : '—'}</span>
                          <span style={{ color: C.dim }}> · Central</span>
                          {e.original_zone && e.original_start && (
                            <span style={{ display: 'block', color: C.dim }} data-testid="ics-row-original">was {e.original_start.replace('T', ' ')} {e.original_zone}</span>
                          )}
                          {e.original_zone && !e.original_start && <span style={{ display: 'block', color: C.dim }}>{e.original_zone}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              {preview.skipped.length > 0 && (
                <div style={{ fontSize: 12, color: C.dim, marginTop: 8 }} data-testid="ics-skipped">
                  Not imported: {preview.skipped.map((s) => `${s.title} (${s.reason})`).join(', ')}
                </div>
              )}
              {!preview.cancel && preview.events.length === 0 && <div style={{ fontSize: 13, color: '#fbbf24', marginTop: 8 }} data-testid="ics-nothing">No events to import in this file.</div>}
            </div>
          )}
          {result && (
            <div data-testid="ics-result" style={{ fontSize: 13, display: 'grid', gap: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{result.cancel ? 'Nothing imported' : `Imported ${result.imported.length} event(s) into ${result.calendar.name}`}</div>
              {result.message && <div style={{ color: '#fde68a' }}>{result.message}</div>}
              <div data-testid="ics-result-counts">
                Imported {result.imported.length} · Skipped {result.skipped.length} · Failed {result.failed.length}
              </div>
              {result.imported.filter((x) => x.kept_zone).map((x) => (
                <div key={x.uid} style={{ color: '#fbbf24' }}>⚠ {x.title}: {x.kept_reason}</div>
              ))}
              {result.skipped.length > 0 && <div style={{ color: C.dim }} data-testid="ics-result-skipped">Skipped: {result.skipped.map((x) => `${x.title} (${x.reason})`).join(', ')}</div>}
              {result.failed.length > 0 && <div style={{ color: '#f87171' }} data-testid="ics-result-failed">Failed: {result.failed.map((x) => `${x.title} (${x.reason})`).join(', ')}</div>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 18px', borderTop: `1px solid ${C.border}` }}>
          {!result && (
            <button type="button" style={btn(C.violet, busy || !preview || preview.cancel || sel.size === 0)} disabled={busy || !preview || preview.cancel || sel.size === 0}
              onClick={run} data-testid="ics-import-run">{busy && preview ? 'Importing…' : `Import ${sel.size || ''}`.trim()}</button>
          )}
          <button type="button" style={btn('#444', busy)} disabled={busy} onClick={result ? onClose : requestClose} data-testid="ics-import-cancel">{result ? 'Close' : 'Cancel'}</button>
        </div>
      </div>
      {prompt}
    </div>
  );
}
